import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useQueryClient, useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  getProviderDetail,
  listProviderDirectory,
  saveProviderProfile,
} from "@/lib/provider-payments.functions";
import {
  CURRENCIES,
  PROVIDER_TYPES,
  PROVIDER_TYPE_LABEL,
  money,
  type ProviderInput,
} from "@/lib/provider-schemas";
import { ErrorBox, ProviderModuleShell, StatCard } from "@/components/admin/provider/ProviderModuleShell";
import { ProviderPriceList } from "@/components/admin/provider/ProviderPriceList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const dirQuery = queryOptions({
  queryKey: ["admin", "provider-directory"],
  queryFn: () => listProviderDirectory(),
});

export const Route = createFileRoute("/_authenticated/admin/provider-management")({
  head: () => ({
    meta: [
      { title: "Proveedores — Administración" },
      { name: "description", content: "Directorio de proveedores, datos de pago y saldos." },
      { property: "og:title", content: "Proveedores — Administración · Mostrando México" },
      { property: "og:description", content: "Administración privada de proveedores por empresa." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  errorComponent: ({ error }) => <ErrorBox error={error as Error} />,
  notFoundComponent: () => <ErrorBox error={new Error("No encontrado")} />,
  component: () => (
    <ProviderModuleShell
      title="Directorio de proveedores"
      description="Datos fiscales y bancarios, tipo de proveedor, moneda de pago y saldo pendiente por proveedor."
    >
      <Body />
    </ProviderModuleShell>
  ),
});

const emptyForm: ProviderInput = {
  name: "",
  provider_type: "operador",
  currency: "MXN",
  default_commission_pct: 0,
  active: true,
};

function Body() {
  const { data } = useSuspenseQuery(dirQuery);
  const qc = useQueryClient();
  const [form, setForm] = useState<ProviderInput | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const save = useMutation({
    mutationFn: (input: ProviderInput) => saveProviderProfile({ data: input }),
    onSuccess: () => {
      toast.success("Proveedor guardado");
      setForm(null);
      qc.invalidateQueries({ queryKey: ["admin", "provider-directory"] });
      qc.invalidateQueries({ queryKey: ["admin", "providers"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const providers = data.providers.filter((p) =>
    search ? `${p.name} ${p.company ?? ""}`.toLowerCase().includes(search.toLowerCase()) : true,
  );
  const pendingByCurrency = new Map<string, number>();
  for (const p of data.providers)
    for (const c of p.balance.currencies)
      pendingByCurrency.set(c.currency, (pendingByCurrency.get(c.currency) ?? 0) + c.pending);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Proveedores" value={data.providers.length} />
        <StatCard label="Activos" value={data.providers.filter((p) => p.active).length} />
        {[...pendingByCurrency.entries()].map(([cur, amount]) => (
          <StatCard key={cur} label={`Pendiente ${cur}`} value={money(amount, cur)} />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Buscar proveedor…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Button onClick={() => setForm({ ...emptyForm })}>Nuevo proveedor</Button>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Proveedor</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Contacto</th>
              <th className="px-3 py-2">Pago</th>
              <th className="px-3 py-2 text-right">Reservas</th>
              <th className="px-3 py-2 text-right">Costo</th>
              <th className="px-3 py-2 text-right">Pagado</th>
              <th className="px-3 py-2 text-right">Pendiente</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <tr key={p.id} className="border-t align-top">
                <td className="px-3 py-2">
                  <div className="font-medium">{p.name}</div>
                  {p.company ? <div className="text-xs text-muted-foreground">{p.company}</div> : null}
                  {!p.active ? (
                    <Badge variant="secondary" className="mt-1">
                      Inactivo
                    </Badge>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-xs">
                  {PROVIDER_TYPE_LABEL[(p.provider_type ?? "otro") as keyof typeof PROVIDER_TYPE_LABEL] ??
                    p.provider_type}
                </td>
                <td className="px-3 py-2 text-xs">
                  <div>{p.contact_name}</div>
                  <div className="text-muted-foreground">{p.contact_email}</div>
                  <div className="text-muted-foreground">{p.contact_phone}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div>{p.payment_method ?? "—"}</div>
                  <div className="text-muted-foreground">{p.bank_name ?? ""}</div>
                  <div className="text-muted-foreground">
                    {p.clabe ? `CLABE ${p.clabe}` : p.bank_account ?? ""}
                  </div>
                  <div className="text-muted-foreground">Moneda: {p.currency ?? "MXN"}</div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{p.balance.bookings}</td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">
                  {p.balance.currencies.map((c: any) => (
                    <div key={`c-${c.currency}`}>{money(c.cost, c.currency)}</div>
                  ))}
                </td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">
                  {p.balance.currencies.map((c: any) => (
                    <div key={`p-${c.currency}`}>{money(c.paid, c.currency)}</div>
                  ))}
                </td>
                <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums">
                  {p.balance.currencies.map((c: any) => (
                    <div key={`q-${c.currency}`} className={c.pending > 0 ? "text-destructive" : ""}>
                      {money(c.pending, c.currency)}
                    </div>
                  ))}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setDetailId(p.id)}>
                      Ver
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setForm({
                          id: p.id,
                          code: p.code,
                          display_alias: p.display_alias,
                          name: (p as typeof p & {original_name?:string}).original_name ?? p.name,
                          company: p.company,
                          provider_type: (p.provider_type ?? "operador") as ProviderInput["provider_type"],
                          contact_name: p.contact_name,
                          contact_email: p.contact_email,
                          contact_phone: p.contact_phone,
                          payment_method: p.payment_method,
                          bank_name: p.bank_name,
                          account_holder: p.account_holder,
                          clabe: p.clabe,
                          bank_account: p.bank_account,
                          currency: (p.currency ?? "MXN") as ProviderInput["currency"],
                          default_commission_pct: Number(p.default_commission_pct ?? 0),
                          active: p.active,
                          notes: p.notes,
                        })
                      }
                    >
                      Editar
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!providers.length ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  Sin proveedores.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[85dvh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle>
          </DialogHeader>
          {form ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Código"><Input value={form.code ?? ""} onChange={e=>setForm({...form,code:e.target.value})}/></Field>
              <Field label="Alias"><Input value={form.display_alias ?? ""} onChange={e=>setForm({...form,display_alias:e.target.value})}/></Field>
              <Field label="Nombre *">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Empresa / razón social">
                <Input
                  value={form.company ?? ""}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </Field>
              <Field label="Tipo">
                <select
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.provider_type}
                  onChange={(e) =>
                    setForm({ ...form, provider_type: e.target.value as ProviderInput["provider_type"] })
                  }
                >
                  {PROVIDER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {PROVIDER_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Moneda de pago">
                <select
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value as ProviderInput["currency"] })}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Contacto">
                <Input
                  value={form.contact_name ?? ""}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                />
              </Field>
              <Field label="Email">
                <Input
                  value={form.contact_email ?? ""}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                />
              </Field>
              <Field label="Teléfono / WhatsApp">
                <Input
                  value={form.contact_phone ?? ""}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                />
              </Field>
              <Field label="Método de pago">
                <Input
                  placeholder="Transferencia, efectivo…"
                  value={form.payment_method ?? ""}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                />
              </Field>
              <Field label="Banco">
                <Input
                  value={form.bank_name ?? ""}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                />
              </Field>
              <Field label="Titular de la cuenta">
                <Input
                  value={form.account_holder ?? ""}
                  onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                />
              </Field>
              <Field label="CLABE">
                <Input value={form.clabe ?? ""} onChange={(e) => setForm({ ...form, clabe: e.target.value })} />
              </Field>
              <Field label="Cuenta / tarjeta">
                <Input
                  value={form.bank_account ?? ""}
                  onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                />
              </Field>
              <Field label="Comisión por defecto (%)">
                <Input
                  type="number"
                  value={form.default_commission_pct}
                  onChange={(e) =>
                    setForm({ ...form, default_commission_pct: Number(e.target.value) || 0 })
                  }
                />
              </Field>
              <Field label="Estado">
                <select
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.active ? "1" : "0"}
                  onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}
                >
                  <option value="1">Activo</option>
                  <option value="0">Inactivo</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Notas internas">
                  <Textarea
                    rows={3}
                    value={form.notes ?? ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </Field>
              </div>
              <div className="sm:col-span-2 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setForm(null)}>
                  Cancelar
                </Button>
                <Button
                  disabled={!form.name.trim() || save.isPending}
                  onClick={() => save.mutate(form)}
                >
                  Guardar
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <ProviderDetail id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function ProviderDetail({ id, onClose }: { id: string | null; onClose: () => void }) {
  const detail = useSuspenseQuery(
    queryOptions({
      queryKey: ["admin", "provider-detail", id ?? "none"],
      queryFn: () => (id ? getProviderDetail({ data: { id } }) : Promise.resolve(null)),
    }),
  );
  const d = detail.data;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85dvh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{d?.provider.name ?? "Proveedor"}</DialogTitle>
        </DialogHeader>
        {d ? (
          <div className="space-y-5 text-sm">
            <ProviderPriceList providerId={d.provider.id}/>
            <div>
              <h3 className="mb-2 font-semibold">Reservas asignadas ({d.bookings.length})</h3>
              <div className="max-h-64 overflow-auto rounded-lg border">
                <table className="w-full text-xs">
                  <tbody>
                    {d.bookings.map((b: any) => (
                      <tr key={b.id} className="border-b">
                        <td className="px-2 py-1">{b.service_date}</td>
                        <td className="px-2 py-1">{b.tour_title}</td>
                        <td className="px-2 py-1">
                          {b.customer_first_name} {b.customer_last_name}
                        </td>
                        <td className="px-2 py-1 text-right">{money(b.provider_cost, b.cost_currency)}</td>
                        <td className="px-2 py-1 text-right">{money(b.pending_amount, b.cost_currency)}</td>
                        <td className="px-2 py-1">{b.payment_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="mb-2 font-semibold">Pagos ({d.payments.length})</h3>
              <div className="max-h-56 overflow-auto rounded-lg border">
                <table className="w-full text-xs">
                  <tbody>
                    {d.payments.map((p: any) => (
                      <tr key={p.id} className="border-b">
                        <td className="px-2 py-1">{p.paid_at}</td>
                        <td className="px-2 py-1">{money(p.amount, p.currency)}</td>
                        <td className="px-2 py-1">{p.method ?? "—"}</td>
                        <td className="px-2 py-1">{p.reference ?? "—"}</td>
                        <td className="px-2 py-1">{p.voided_at ? "Anulado" : "Aplicado"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
